import { useEffect, useMemo, useRef, useState } from "react";
import type { AlgorithmOperation } from "@algoquest/schemas";
import { GameCanvas, type GameCanvasHandle } from "./core/GameCanvas";
import { HUD, type SortAlgorithm } from "./ui/HUD";
import { LoadingScreen } from "./ui/LoadingScreen";
import { loadLevel, type LoadedLevel } from "./levels/levelLoader";
import { useSessionStore } from "./stores/sessionStore";
import { useSearchAlgorithmStore } from "./stores/searchAlgorithmStore";
import { useSortAlgorithmStore } from "./stores/sortAlgorithmStore";
import type { AlgorithmMode, SearchViewState } from "./algorithms/searching/SearchController";
import type { SortViewState } from "./algorithms/sorting/SortController";

/**
 * Phase 14 (Master Spec §33/§34/§36 — "Workshop 3D editor") — a
 * read-only 3D preview of a single Workshop `LevelDefinition`, reusing
 * `GameCanvas`/`GameRuntime`/`HUD` completely unchanged. This is the
 * literal reading of §36's "build the editor using the same rendering
 * foundation [as the Three.js game]... the editor and runtime should
 * consume the same level format" — not a second renderer, not a forked
 * Box/CameraRig, but the EXACT SAME entity classes and game runtime a
 * real player uses, loading whatever `LevelDefinition` a teacher is
 * currently authoring in `apps/web`'s Phase 13 JSON editor.
 *
 * **Design decision, made explicitly:** the level data arrives entirely
 * through the `workshopPreview` URL query parameter (a JSON-encoded
 * `{ title, definition }` payload — see `apps/web/lib/workshopPreviewUrl.ts`),
 * not through an authenticated API call. This app never touches
 * `authClient`/`httpClient`/auth state at all. Two reasons: (1) the
 * whole point is to preview content that may not even be saved yet —
 * `apps/web`'s "Preview in 3D" button sends whatever is currently typed
 * and Zod-valid in the textarea, matching §36's own pipeline order
 * (LevelDefinition JSON → Zod Validation → Preview/Test, BEFORE
 * Publish); (2) it avoids inventing cross-app session sharing between
 * two different dev-server origins (`apps/web` on :3000, `game/` on
 * :5173) for a feature that doesn't need one — no new backend route,
 * no new CORS/cookie plumbing, nothing §49 would call premature. The
 * trade-off, stated rather than hidden: the preview URL contains the
 * full level JSON in its query string. That's fine for its actual use
 * (a teacher opens it in a new tab immediately after clicking a button
 * on a page only they can reach); it is NOT a durable or shareable
 * link — nothing generates or displays this URL for the user to copy,
 * and very large level definitions could in theory exceed a browser's
 * practical URL length, which isn't a concern at Workshop V1's actual
 * content scale (a handful of numbers and short strings).
 *
 * No attempt is ever submitted from here (there is no signed-in user to
 * attribute one to) — `onViewStep`/`onSortViewStep` only update the
 * Zustand stores `HUD` reads from, mirroring `App.tsx`'s handlers minus
 * the `submitCompletedAttempt` call entirely.
 */
export interface WorkshopPreviewAppProps {
  encoded: string;
}

interface PreviewPayload {
  title: string;
  definition: unknown;
}

function decodePayload(encoded: string): PreviewPayload | null {
  // `encoded` already comes out of `URLSearchParams.get(...)` (both here
  // and in `apps/web`'s `buildWorkshopPreviewUrl`, which builds the
  // query string via `URLSearchParams` too) — that API decodes
  // percent-encoding itself, so this is already the raw JSON text.
  // Calling `decodeURIComponent` again here would double-decode it and
  // break on any JSON content that happens to contain a literal `%`.
  try {
    const parsed: unknown = JSON.parse(encoded);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "definition" in parsed &&
      "title" in (parsed as Record<string, unknown>)
    ) {
      const candidate = parsed as { title: unknown; definition: unknown };
      if (typeof candidate.title === "string") {
        return { title: candidate.title, definition: candidate.definition };
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function WorkshopPreviewApp({ encoded }: WorkshopPreviewAppProps): React.JSX.Element {
  const gameRef = useRef<GameCanvasHandle | null>(null);

  const isPaused = useSessionStore((state) => state.isPaused);
  const setPaused = useSessionStore((state) => state.setPaused);
  const setNearestBoxIndex = useSessionStore((state) => state.setNearestBoxIndex);

  const setView = useSearchAlgorithmStore((state) => state.setView);
  const appendSearchTrace = useSearchAlgorithmStore((state) => state.appendTrace);
  const setSearchRejectedHint = useSearchAlgorithmStore((state) => state.setRejectedHint);
  const setChoiceRejected = useSearchAlgorithmStore((state) => state.setChoiceRejected);
  const resetSearchAlgorithm = useSearchAlgorithmStore((state) => state.reset);

  const setSortView = useSortAlgorithmStore((state) => state.setView);
  const appendSortTrace = useSortAlgorithmStore((state) => state.appendTrace);
  const setSortRejectedHint = useSortAlgorithmStore((state) => state.setRejectedHint);
  const setDecisionRejected = useSortAlgorithmStore((state) => state.setDecisionRejected);
  const resetSortAlgorithm = useSortAlgorithmStore((state) => state.reset);

  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{ title: string; level: LoadedLevel } | null>(null);

  // Decoded and Zod-validated exactly once — this is a fresh trust
  // boundary (Master Spec §10: an arbitrary URL is at least as untrusted
  // as a network response), so `loadLevel` (the same boundary-validation
  // function every fixture and every synced level already goes through)
  // is not skipped just because the value came from `apps/web`'s own
  // already-client-validated form.
  useEffect(() => {
    const payload = decodePayload(encoded);
    if (!payload) {
      setError("This preview link is missing or malformed level data.");
      return;
    }
    try {
      const level = loadLevel(payload.definition);
      setLoaded({ title: payload.title, level });
    } catch (err) {
      setError(err instanceof Error ? err.message : "This level definition failed validation.");
    }
    // Runs once — `encoded` is the URL query string at mount, not
    // something this page ever expects to change underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived from `dataset.kind`, not `coordinates.stage` — `GameRuntime
  // .loadLevel` itself switches on `dataset.kind` to decide which engine
  // to run (see `game/src/engine/GameRuntime.ts`), so this HUD display
  // must key off the exact same field or it could show a stage/algorithm
  // label that disagrees with what's actually about to run. (A
  // `LevelDefinition` where `coordinates.stage` and `dataset.kind`
  // disagree is schema-valid but semantically malformed — Zod and
  // `validateLevelDefinition` don't cross-check those two fields against
  // each other, a pre-existing Phase 00 gap this phase doesn't attempt
  // to close. `GameRuntime.loadLevel` already throws if `algorithm`
  // doesn't belong to `dataset.kind`'s allowed set, which is this
  // codebase's existing last line of defense for every level source,
  // fixture or synced — not something new to preview mode.)
  const dataset = loaded?.level.definition.dataset;
  const stage: "searching" | "sorting" = dataset?.kind ?? "searching";
  const algorithm = loaded?.level.definition.algorithm;
  const mode: AlgorithmMode = stage === "searching" && algorithm ? (algorithm as AlgorithmMode) : "linear-search";
  const sortAlgorithm: SortAlgorithm = stage === "sorting" && algorithm ? (algorithm as SortAlgorithm) : "bubble-sort";
  const target = dataset?.kind === "searching" ? dataset.target : 0;
  const sortDirection = dataset?.kind === "sorting" ? dataset.sortDirection : "ascending";
  const levelLabel = useMemo(() => (loaded ? `Preview — ${loaded.title}` : ""), [loaded]);

  const handleViewReset = (view: SearchViewState) => {
    resetSearchAlgorithm(view);
  };

  const handleViewStep = (view: SearchViewState, operations: AlgorithmOperation[]) => {
    setView(view);
    appendSearchTrace(operations);
  };

  const handleActionRejected = (expectedIndex: number) => {
    setSearchRejectedHint(expectedIndex);
  };

  const handleChoiceRejected = () => {
    setChoiceRejected(true);
    window.setTimeout(() => setChoiceRejected(false), 100);
  };

  const handleSortViewReset = (view: SortViewState) => {
    resetSortAlgorithm(view);
  };

  const handleSortViewStep = (view: SortViewState, operations: AlgorithmOperation[]) => {
    setSortView(view);
    appendSortTrace(operations);
  };

  const handleSortActionRejected = (expectedIndex: number) => {
    setSortRejectedHint(expectedIndex);
  };

  const handleSortDecisionRejected = () => {
    setDecisionRejected(true);
    window.setTimeout(() => setDecisionRejected(false), 100);
  };

  const handleTogglePause = () => {
    const next = !isPaused;
    setPaused(next);
    if (next) {
      gameRef.current?.pause();
    } else {
      gameRef.current?.resume();
    }
  };

  const handleRestart = () => {
    gameRef.current?.reset();
    setPaused(false);
  };

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-900 p-6 text-center text-slate-100">
        <div>
          <p className="text-lg font-semibold">Can&apos;t preview this level</p>
          <p className="mt-2 text-sm text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return <LoadingScreen message="Loading preview…" />;
  }

  return (
    <div className="preview-root relative h-screen w-screen overflow-hidden bg-slate-900">
      <div className="absolute inset-x-0 top-0 z-10 bg-sky-700/90 px-4 py-1 text-center text-xs text-white">
        Workshop preview · Progress is not saved.
      </div>
      <GameCanvas
        ref={gameRef}
        initialLevel={loaded.level}
        onNearestBoxChange={setNearestBoxIndex}
        onViewReset={handleViewReset}
        onViewStep={handleViewStep}
        onActionRejected={handleActionRejected}
        onChoiceRejected={handleChoiceRejected}
        onSortViewReset={handleSortViewReset}
        onSortViewStep={handleSortViewStep}
        onSortActionRejected={handleSortActionRejected}
        onSortDecisionRejected={handleSortDecisionRejected}
      />
      <HUD
        stage={stage}
        mode={mode}
        sortAlgorithm={sortAlgorithm}
        levelLabel={levelLabel}
        target={target}
        sortDirection={sortDirection}
        levelIndex={0}
        levelCount={1}
        showLevelSwitcher={false}
        onTogglePause={handleTogglePause}
        onRestart={handleRestart}
        onResetCamera={() => gameRef.current?.resetCamera()}
        onPrevLevel={() => {}}
        onNextLevel={() => {}}
        onSwitchStage={() => {}}
        onSwitchMode={() => {}}
        onSwitchSortAlgorithm={() => {}}
      />
    </div>
  );
}
